import React from 'react';
import { UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faUser } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { ApprovalRequestView } from '../../../../../services/approvals/approvals.service';
import { Button } from '../../../../../components/Button/Button';
import classes from './approvals-modal.module.scss';

const ACTION_LABELS: Record<string, string> = {
  global_discount: 'הנחה (כללית)',
  item_discount: 'הנחה לפריטים',
  cancel: 'ביטול',
  on_the_house: 'על חשבון הבית',
  general_item: 'פריט כללי',
};

const getActionLabel = (req: ApprovalRequestView): string => {
  if (req.payload.actionType === 'cancel' && req.payload.cancelScope === 'order') {
    return 'ביטול כל ההזמנה';
  }
  return ACTION_LABELS[req.actionType] ?? req.actionType;
};

const REASON_LABELS: Record<string, string> = {
  cancel_order: 'ביטול הזמנה',
  vip: 'VIP',
  waiter_error: 'טעות במנה',
  staff: 'שת"פ',
  other: 'אחר',
  manager_action: 'פעולת מנהל',
};

const formatMoney = (value: number): string => `₪${value.toFixed(1).replace(/\.0$/, '')}`;

const formatRequestDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getRequestDetail = (req: ApprovalRequestView): string => {
  switch (req.payload.actionType) {
    case 'global_discount':
      return req.payload.discountMode === 'percentage'
        ? `${req.payload.discountInput}% - הנחה על כל ההזמנה, כולל מוצרים שייתווספו בהמשך`
        : formatMoney(req.payload.discountInput);
    case 'item_discount': {
      const total = req.payload.items.reduce((sum, item) => sum + item.discountInput, 0);
      return formatMoney(total);
    }
    case 'on_the_house': {
      const total = req.payload.items.reduce((sum, item) => sum + item.discountInput, 0);
      return formatMoney(total);
    }
    case 'cancel': {
      const orderlineById = new Map(req.orderlines.map((line) => [line.orderlineId, line]));
      const total = req.payload.items.reduce((sum, item) => (
        sum + (orderlineById.get(item.orderlineId)?.unitPrice ?? 0) * item.quantity
      ), 0);
      return formatMoney(total);
    }
    case 'general_item':
      return `${req.payload.quantity} × ${formatMoney(req.payload.unitPrice)}`;
    default:
      return '';
  }
};

const getVisibleOrderlines = (req: ApprovalRequestView): ApprovalRequestView['orderlines'] => {
  const orderlineById = new Map(req.orderlines.map((line) => [line.orderlineId, line]));

  switch (req.payload.actionType) {
    case 'item_discount':
    case 'on_the_house':
    case 'cancel':
      return req.payload.items.map((item) => {
        const orderline = orderlineById.get(item.orderlineId);
        return {
          orderlineId: item.orderlineId,
          productName: orderline?.productName ?? null,
          quantity: item.quantity,
          unitPrice: orderline?.unitPrice ?? 0,
        };
      });
    case 'general_item':
      return [{
        orderlineId: -req.id,
        productName: 'פריט כללי',
        quantity: req.payload.quantity,
        unitPrice: req.payload.unitPrice,
      }];
    case 'global_discount':
    default:
      return req.orderlines;
  }
};

type Props = {
  opened: boolean;
  onClose: () => void;
  canClose?: boolean;
  requests: ApprovalRequestView[];
  mode?: 'pending' | 'history';
  historyFilter?: 'all' | 'approved' | 'denied';
  onHistoryFilterChange?: (filter: 'all' | 'approved' | 'denied') => void;
  isLoading: boolean;
  isDeciding: boolean;
  hasMoreHistory?: boolean;
  onLoadMoreHistory?: () => void;
  onDecide: (requestId: number, decision: 'approved' | 'denied') => Promise<void>;
};

export const ApprovalsModal: React.FC<Props> = ({
  opened,
  onClose,
  canClose = true,
  requests,
  mode = 'pending',
  historyFilter = 'all',
  onHistoryFilterChange = () => undefined,
  isLoading,
  isDeciding,
  hasMoreHistory = false,
  onLoadMoreHistory = () => undefined,
  onDecide,
}) => {
  if (!opened) return null;

  return (
    <div className={classes.overlay} data-testid="approvals-overlay" onClick={() => { if (canClose && !isDeciding) onClose(); }}>
      <div className={classes.modal} data-testid="approvals-modal" onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            {canClose ? (
              <UnstyledButton className={classes.closeBtn} type="button" data-testid="approvals-close" disabled={isDeciding} onClick={onClose}>{'×'}</UnstyledButton>
            ) : null}
          </div>
          <div className={classes.headerCenter}>
            <h2 className={classes.title}>{mode === 'pending' ? 'בקשות ממתינות' : 'היסטוריית פעולות'}</h2>
          </div>
        </div>

        <div className={classes.body}>
          {mode === 'history' && (
            <div className={classes.historyFilters} role="group" aria-label="סינון היסטוריית בקשות">
              {([
                ['all', 'הכל'],
                ['approved', 'אושרו'],
                ['denied', 'נדחו'],
              ] as const).map(([filter, label]) => (
                <UnstyledButton
                  key={filter}
                  type="button"
                  className={classes.historyFilter}
                  data-active={historyFilter === filter}
                  data-testid={`approvals-history-filter-${filter}`}
                  onClick={() => onHistoryFilterChange(filter)}
                >
                  {label}
                </UnstyledButton>
              ))}
            </div>
          )}
          {isLoading && <div className={classes.empty}>{'טוען...'}</div>}
          {!isLoading && requests.length === 0 && (
            <div className={classes.empty}>{mode === 'pending' ? 'אין בקשות ממתינות' : 'אין בקשות שאושרו או נדחו במשמרת זו'}</div>
          )}
          {requests.map((req) => {
            const visibleOrderlines = getVisibleOrderlines(req);
            return (
            <div key={req.id} className={classes.card} data-testid={`approvals-card-${req.id}`}>
              <div className={classes.cardTop}>
                <div className={classes.orderHeading}>
                  <div className={classes.orderTitle}>
                    <span>{(req.orderCode ?? String(req.orderId)).replace(/^[^/]+\//, '')}</span>
                    <span className={classes.tableBadge}>{req.tableLabel ? `שולחן ${req.tableLabel}` : 'ללא שולחן'}</span>
                  </div>
                  <div className={classes.requestTime}>
                    <span>{formatRequestDateTime(req.createdAt)}</span>
                  </div>
                </div>
                <span className={classes.statusBadge} data-status={req.status}>
                  {req.status === 'pending' ? 'ממתין' : req.status === 'approved' ? 'אושר' : 'נדחה'}
                </span>
              </div>

              <div className={classes.meta}>
                <div className={classes.metaRow}>
                  <span className={classes.actionBadge}>{getActionLabel(req)}</span>
                </div>
                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>{'סיבה:'}</span>
                  <span className={classes.metaValue}>{REASON_LABELS[req.reason] ?? req.reason}</span>
                </div>
                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>{'מבוקש על ידי:'}</span>
                  <span className={classes.metaValue}>
                    <FontAwesomeIcon icon={faUser as IconProp} />
                    {req.requestedByName}
                  </span>
                </div>
                {req.status !== 'pending' && (
                  <div className={classes.metaRow}>
                    <span className={classes.metaLabel}>
                      {req.status === 'approved' ? 'אושר ע״י:' : 'נדחה ע״י:'}
                    </span>
                    <span className={classes.metaValue}>
                      <FontAwesomeIcon icon={faUser as IconProp} />
                      {req.decidedByName ?? '-'}
                    </span>
                  </div>
                )}
                <div className={classes.metaRow}>
                  <span className={classes.metaLabel}>{'עלות תפעול:'}</span>
                  <span className={classes.metaValue}>{getRequestDetail(req)}</span>
                </div>
                {req.note && (
                  <div className={classes.metaRow}>
                    <span className={classes.metaLabel}>{'הערה:'}</span>
                    <span className={classes.metaValue}>{req.note}</span>
                  </div>
                )}
              </div>

              {visibleOrderlines.length > 0 && (
                <div className={classes.orderlines}>
                  <div className={classes.orderlinesTitle}>
                    <FontAwesomeIcon icon={faList as IconProp} />
                    {'פריטי הזמנה'}
                  </div>
                  {visibleOrderlines.map((ol) => (
                    <div key={ol.orderlineId} className={classes.orderlineRow}>
                      <span className={classes.orderlineName}>{ol.productName ?? 'פריט כללי'}</span>
                      <span className={classes.orderlineQty}>{ol.quantity}</span>
                    </div>
                  ))}
                </div>
              )}

              {req.status === 'pending' && (
                <div className={classes.actions}>
                  <Button
                    variant="cancel"
                    onClick={() => onDecide(req.id, 'denied')}
                    disabled={isDeciding}
                    dataTestId={`approvals-deny-${req.id}`}
                    fullWidth
                  >
                    {'ביטול'}
                  </Button>
                  <Button
                    variant="confirm"
                    onClick={() => onDecide(req.id, 'approved')}
                    disabled={isDeciding}
                    loading={isDeciding}
                    dataTestId={`approvals-approve-${req.id}`}
                    fullWidth
                  >
                    {'אישור'}
                  </Button>
                </div>
              )}
            </div>
            );
          })}
          {mode === 'history' && hasMoreHistory && (
            <Button variant="save" onClick={onLoadMoreHistory} disabled={isLoading || isDeciding} dataTestId="approvals-load-more" fullWidth>
              {'טען עוד'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
